import React, { useState, useEffect } from 'react';
import { FiLoader, FiFileText, FiRefreshCw, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import useEditorStore from '../../../stores/useEditorStore';
import { loadAllEditorDocuments, loadEditorDocument } from '../../../services/firestoreService';
import { deleteTemplates } from '../../../services/editorService';
import { convertSlide } from '../../../services/parserConverter';

const TemplateSettingsPanel = () => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDocId, setLoadingDocId] = useState(null);
  const [error, setError] = useState(null);

  // 다중 선택 모드
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    canvas,
    setCanvasSize,
    pushHistory,
    syncLayersFromCanvas,
    setActiveTool,
    setSlidesData,
    triggerCenter,
  } = useEditorStore();

  const fetchDocuments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await loadAllEditorDocuments();
      if (result.success) {
        setDocuments(result.documents);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleSelectTemplate = async (docId) => {
    if (selectMode) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(docId)) next.delete(docId);
        else next.add(docId);
        return next;
      });
      return;
    }

    if (!canvas || loadingDocId) return;
    setLoadingDocId(docId);

    try {
      const docResult = await loadEditorDocument(docId, { useThumbnail: true });
      if (!docResult.success) throw new Error(docResult.error || '문서 로드 실패');

      const { data } = docResult;
      canvas.clear();
      canvas.backgroundColor = '#ffffff';

      const [canvasWidth, canvasHeight] = data.canvas_size;
      setCanvasSize({ width: canvasWidth, height: canvasHeight });

      const slidesForStore = data.slides.map(slide => ({
        layers: slide.layers,
        canvasState: null,
      }));
      setSlidesData(slidesForStore, docId);

      if (data.slides && data.slides.length > 0) {
        const firstSlide = data.slides[0];
        const fabricObjects = await convertSlide(firstSlide.layers);
        for (const obj of fabricObjects) {
          canvas.add(obj);
        }
      }

      canvas.renderAll();
      syncLayersFromCanvas();
      pushHistory();
      triggerCenter();
      setActiveTool('select');
    } catch (err) {
      console.error('Template load error:', err);
      alert(`템플릿 로드 오류: ${err.message}`);
    } finally {
      setLoadingDocId(null);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return;
    const idsToDelete = [...selectedIds];

    // 즉시 UI 반영: 목록에서 제거, 모달 닫기, 선택 모드 종료
    setDocuments(prev => prev.filter(d => !selectedIds.has(d.id)));
    setSelectedIds(new Set());
    setSelectMode(false);
    setShowDeleteModal(false);

    // 백그라운드에서 서버 삭제 (실패 시 콘솔 경고)
    deleteTemplates(idsToDelete).then(result => {
      if (!result.success) {
        console.error('템플릿 삭제 오류:', result.error);
      }
    }).catch(err => {
      console.error('템플릿 삭제 오류:', err.message);
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === documents.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(documents.map(d => d.id)));
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getFileTypeLabel = (type) => {
    switch (type) {
      case 'svg': return 'SVG';
      case 'ppt': return 'PPT';
      case 'psd': return 'PSD';
      default: return type?.toUpperCase() || '';
    }
  };

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>템플릿</h3>
        <div style={{ display: 'flex', gap: '4px' }}>
          {selectMode ? (
            <>
              <button onClick={toggleSelectAll} style={headerBtnStyle} title="전체 선택">
                <FiCheck size={16} />
              </button>
              <button onClick={exitSelectMode} style={headerBtnStyle} title="선택 취소">
                <FiX size={16} />
              </button>
            </>
          ) : (
            <>
              {documents.length > 0 && (
                <button onClick={() => setSelectMode(true)} style={headerBtnStyle} title="선택 삭제">
                  <FiTrash2 size={16} />
                </button>
              )}
              <button onClick={fetchDocuments} disabled={isLoading} style={headerBtnStyle} title="새로고침">
                <FiRefreshCw size={16} style={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} />
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* 선택 모드 안내 바 */}
      {selectMode && (
        <div style={selectBarStyle}>
          <span style={{ fontSize: '12px', color: '#3b82f6' }}>
            {selectedIds.size}개 선택됨
          </span>
          <button
            onClick={() => selectedIds.size > 0 && setShowDeleteModal(true)}
            disabled={selectedIds.size === 0}
            style={{
              ...deleteBtnStyle,
              opacity: selectedIds.size === 0 ? 0.4 : 1,
            }}
          >
            삭제
          </button>
        </div>
      )}

      {isLoading && documents.length === 0 && (
        <div style={loadingContainerStyle}>
          <FiLoader size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#6b7280' }}>템플릿 로드 중...</p>
        </div>
      )}

      {error && (
        <div style={errorStyle}>
          <p>{error}</p>
          <button onClick={fetchDocuments} style={retryButtonStyle}>다시 시도</button>
        </div>
      )}

      {!isLoading && documents.length === 0 && !error && (
        <div style={emptyStyle}>
          <FiFileText size={32} color="#9ca3af" />
          <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#6b7280' }}>저장된 템플릿이 없습니다</p>
        </div>
      )}

      <div style={listContainerStyle}>
        {documents.map((doc) => (
          <div
            key={doc.id}
            onClick={() => !loadingDocId && handleSelectTemplate(doc.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && !loadingDocId && handleSelectTemplate(doc.id)}
            style={{
              ...templateItemStyle,
              opacity: loadingDocId && loadingDocId !== doc.id ? 0.5 : 1,
              cursor: loadingDocId ? 'not-allowed' : 'pointer',
              borderColor: selectMode && selectedIds.has(doc.id) ? '#3b82f6' : '#e5e7eb',
              backgroundColor: selectMode && selectedIds.has(doc.id) ? '#eff6ff' : '#ffffff',
            }}
          >
            {/* 체크박스 (선택 모드) */}
            {selectMode && (
              <div style={{
                width: '20px', height: '20px', borderRadius: '4px', flexShrink: 0,
                border: selectedIds.has(doc.id) ? '2px solid #3b82f6' : '2px solid #d1d5db',
                backgroundColor: selectedIds.has(doc.id) ? '#3b82f6' : '#ffffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {selectedIds.has(doc.id) && <FiCheck size={12} color="#ffffff" />}
              </div>
            )}

            <div style={thumbnailStyle}>
              {loadingDocId === doc.id ? (
                <FiLoader size={20} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <FiFileText size={20} color="#6b7280" />
              )}
            </div>

            <div style={infoStyle}>
              <div style={typeTagStyle}>{getFileTypeLabel(doc.file_type)}</div>
              {doc.filename && <div style={filenameStyle}>{doc.filename}</div>}
              <div style={sizeStyle}>{doc.canvas_size_x}×{doc.canvas_size_y}</div>
              <div style={dateStyle}>{formatDate(doc.created_at)}</div>
              {doc.slide_count > 1 && <div style={slideCountStyle}>{doc.slide_count}개 슬라이드</div>}
            </div>
          </div>
        ))}
      </div>

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div style={modalOverlayStyle} onClick={() => setShowDeleteModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h4 style={{ margin: '0 0 12px', fontSize: '15px', color: '#1f2937' }}>템플릿 삭제</h4>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#6b7280' }}>
              선택한 {selectedIds.size}개의 템플릿을 삭제하시겠습니까?<br />
              <span style={{ color: '#dc2626', fontSize: '12px' }}>이 작업은 되돌릴 수 없습니다.</span>
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDeleteModal(false)} style={modalCancelBtnStyle}>
                취소
              </button>
              <button onClick={handleDeleteSelected} style={modalDeleteBtnStyle}>
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const panelStyle = { padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const headerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' };
const titleStyle = { fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 };
const headerBtnStyle = { width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', borderRadius: '6px', backgroundColor: '#ffffff', color: '#6b7280', cursor: 'pointer' };
const selectBarStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', marginBottom: '8px', backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe' };
const deleteBtnStyle = { padding: '4px 12px', fontSize: '12px', fontWeight: '500', border: 'none', borderRadius: '4px', backgroundColor: '#ef4444', color: '#ffffff', cursor: 'pointer' };
const loadingContainerStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', color: '#6b7280' };
const errorStyle = { padding: '16px', backgroundColor: '#fef2f2', borderRadius: '8px', color: '#dc2626', fontSize: '13px', textAlign: 'center' };
const retryButtonStyle = { marginTop: '8px', padding: '6px 12px', border: '1px solid #dc2626', borderRadius: '4px', backgroundColor: 'transparent', color: '#dc2626', fontSize: '12px', cursor: 'pointer' };
const emptyStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center' };
const listContainerStyle = { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' };
const templateItemStyle = { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#ffffff', textAlign: 'left', transition: 'all 0.15s', width: '100%', cursor: 'pointer' };
const thumbnailStyle = { width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', borderRadius: '6px', flexShrink: 0 };
const infoStyle = { flex: 1, minWidth: 0 };
const typeTagStyle = { display: 'inline-block', padding: '2px 6px', backgroundColor: '#e0e7ff', color: '#4338ca', fontSize: '10px', fontWeight: '600', borderRadius: '4px', marginBottom: '4px' };
const filenameStyle = { fontSize: '13px', fontWeight: '500', color: '#1f2937', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const sizeStyle = { fontSize: '12px', fontWeight: '400', color: '#6b7280' };
const dateStyle = { fontSize: '11px', color: '#9ca3af', marginTop: '2px' };
const slideCountStyle = { fontSize: '11px', color: '#6b7280', marginTop: '2px' };
const modalOverlayStyle = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };
const modalStyle = { backgroundColor: '#ffffff', borderRadius: '12px', padding: '24px', minWidth: '300px', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const modalCancelBtnStyle = { padding: '8px 16px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#ffffff', color: '#374151', cursor: 'pointer' };
const modalDeleteBtnStyle = { padding: '8px 16px', fontSize: '13px', border: 'none', borderRadius: '6px', backgroundColor: '#ef4444', color: '#ffffff', cursor: 'pointer' };

export default TemplateSettingsPanel;
